import { Box, Typography, Stack } from "@mui/material";

import rankBadge1 from "../../../images/rank_badge/rank_badge_1.png";
import rankBadge2 from "../../../images/rank_badge/rank_badge_2.png";
import rankBadge3 from "../../../images/rank_badge/rank_badge_3.png";

const MAIN_FONT = "Noto Sans TC";

const BAR_GRADIENTS = {
  1: "linear-gradient(to right, #6B21C8, #C8910A, #FFD700)",
  2: "linear-gradient(to right, #2D2D3A, #5B7FA0, #A8C8E0)",
  3: "linear-gradient(to right, #8B0000, #CC2200, #FF5500)",
  4: "linear-gradient(to right, #5C3A10, #A07030, #C89050)",
  5: "linear-gradient(to right, #4A1010, #8B2020, #AA4040)",
};

const getBarGradient = (rank) => BAR_GRADIENTS[rank] || BAR_GRADIENTS[5];

const BADGE_MAP = { 1: rankBadge1, 2: rankBadge2, 3: rankBadge3 };

const RankBadge = ({ rank }) => {
  if (rank >= 1 && rank <= 3) {
    return (
      <Box
        component="img"
        src={BADGE_MAP[rank]}
        alt={`rank ${rank}`}
        sx={{ width: 64, height: 64, objectFit: "contain", flexShrink: 0 }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #4B2A8A, #7B4FBB)",
        border: "2px solid #8B6FCC",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: "0 0 8px rgba(139, 111, 204, 0.6)",
      }}
    >
      <Typography
        sx={{
          fontFamily: MAIN_FONT,
          fontSize: "24px",
          fontWeight: "900",
          color: "#E0D0FF",
        }}
      >
        {rank}
      </Typography>
    </Box>
  );
};

const LeaderboardRow = ({ item, bestScore }) => {
  const barPercent =
    bestScore > 0
      ? Math.min(Math.floor((item.score / bestScore) * 100), 100)
      : 0;

  const barGlow =
    item.rank === 1
      ? "0 0 10px rgba(255, 215, 0, 0.5)"
      : item.rank === 2
        ? "0 0 8px rgba(160, 200, 224, 0.4)"
        : item.rank === 3
          ? "0 0 8px rgba(255, 85, 0, 0.4)"
          : "none";

  return (
    <Box display="flex" alignItems="center" gap={2} sx={{ width: "100%", py: 1 }}>
      <RankBadge rank={item.rank} />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* Bar + score */}
        <Box sx={{ position: "relative", mb: 0.5 }}>
          <Box
            sx={{
              width: `${barPercent}%`,
              minWidth: 40,
              height: 28,
              background: getBarGradient(item.rank),
              borderRadius: "4px",
              boxShadow: barGlow,
            }}
          />
          <Typography
            sx={{
              fontFamily: MAIN_FONT,
              fontSize: "14px",
              fontWeight: "700",
              color: "#FFFFFF",
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translate(0px, -50%)",
              whiteSpace: "nowrap",
            }}
          >
            {item.scoreLabel ?? item.score}
          </Typography>
        </Box>

        {/* Name */}
        <Typography
          sx={{
            fontFamily: MAIN_FONT,
            fontSize: "18px",
            fontWeight: "700",
            color: "#00E5FF",
            textShadow: "0 0 8px rgba(0, 229, 255, 0.5)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.itemName}
        </Typography>
      </Box>
    </Box>
  );
};

const RowDivider = () => (
  <Box
    sx={{
      width: "100%",
      height: "1px",
      background:
        "linear-gradient(to right, transparent, rgba(150, 100, 220, 0.4), transparent)",
      my: 0.5,
    }}
  />
);

/**
 * RankingLeaderboard — pure visual component.
 *
 * Props:
 *   heading     {string}   — top title line
 *   subHeading  {string}   — larger subtitle line
 *   leaderboardData {Array<{ id, itemName, rank, score }>}
 *   isLoading   {boolean}
 */
export default function RankingLeaderboard({
  heading = "",
  subHeading = "",
  leaderboardData = [],
  isLoading = false,
  onSubHeadingClick = undefined,
}) {
  const bestScore =
    leaderboardData.length > 0
      ? Math.max(...leaderboardData.map((d) => d.score), 1)
      : 1;

  // console.log("Rendering RankingLeaderboard with data:", leaderboardData);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundImage: `
          radial-gradient(ellipse at top, #0D1B3E 0%, #060D1F 70%),
          repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(0,150,255,0.06) 40px),
          repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(0,150,255,0.06) 40px)
        `,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Stack gap={2} direction="column" sx={{
        alignContent: "center",
        textAlign: "center", mb: 2
      }}>
        <Typography
          sx={{
            fontFamily: MAIN_FONT,
            fontSize: "25px",
            fontWeight: "900",
            background: "linear-gradient(90deg, #00FF88, #00E5FF)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "0.05em",
          }}
        >
          {heading}
        </Typography>
        <Typography
          sx={{
            fontFamily: MAIN_FONT,
            fontSize: "20px",
            fontWeight: "900",
            color: "#00E5FF",
            textShadow: "0 0 20px rgba(0, 229, 255, 0.7)",
            letterSpacing: "0.03em",
            cursor: onSubHeadingClick ? "pointer" : "default",
            userSelect: onSubHeadingClick ? "none" : "auto",
          }}
          onClick={onSubHeadingClick}
        >
          {subHeading}{onSubHeadingClick ? " ▼" : ""}
        </Typography>
      </Stack>

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 700,
          border: "2px solid rgba(130, 60, 255, 0.7)",
          borderRadius: "16px",
          background: "rgba(10, 5, 30, 0.7)",
          boxShadow:
            "0 0 20px rgba(130, 60, 255, 0.3), inset 0 0 20px rgba(130, 60, 255, 0.05)",
          p: 2,
          px: 3,
        }}
      >
        {isLoading && (
          <Typography
            sx={{
              fontFamily: MAIN_FONT,
              color: "#00E5FF",
              fontSize: "20px",
              textAlign: "center",
              py: 4,
            }}
          >
            資料載入中...
          </Typography>
        )}

        {!isLoading && leaderboardData.length === 0 && (
          <Typography
            sx={{
              fontFamily: MAIN_FONT,
              color: "#8888AA",
              fontSize: "20px",
              textAlign: "center",
              py: 4,
            }}
          >
            未有資料
          </Typography>
        )}

        {!isLoading && leaderboardData.length > 0 && (
          <Stack>
            {leaderboardData.map((item, index) => (
              <Box key={item.id}>
                {index > 0 && <RowDivider />}
                <LeaderboardRow item={item} bestScore={bestScore} />
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
